"use client"

import { useEffect } from "react"
import { motion } from "framer-motion"
import "bootstrap-icons/font/bootstrap-icons.css"
import { articles } from "./Blog"
import "./BlogArticle.css"

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement("meta")
    const [key, name] = attribute
    element.setAttribute(key, name)
    document.head.appendChild(element)
  }

  element.setAttribute("content", value)
}

const BlogArticle = ({ slug }) => {
  const article = articles.find((item) => item.slug === slug)

  useEffect(() => {
    if (!article) return undefined

    const previousTitle = document.title
    const imageUrl = new URL(article.image, window.location.origin).toString()
    document.title = `${article.title} | Katchit Blog`
    setMeta('meta[name="description"]', ["name", "description"], article.excerpt)
    setMeta('meta[property="og:title"]', ["property", "og:title"], article.title)
    setMeta('meta[property="og:description"]', ["property", "og:description"], article.excerpt)
    setMeta('meta[property="og:image"]', ["property", "og:image"], imageUrl)
    setMeta('meta[name="twitter:title"]', ["name", "twitter:title"], article.title)
    setMeta('meta[name="twitter:description"]', ["name", "twitter:description"], article.excerpt)
    setMeta('meta[name="twitter:image"]', ["name", "twitter:image"], imageUrl)

    return () => {
      document.title = previousTitle
    }
  }, [article])

  if (!article) {
    return (
      <main className="article-not-found">
        <div>
          <span>404</span>
          <h1>We couldn't find that story.</h1>
          <a href="/#blog">Back to all blogs</a>
        </div>
      </main>
    )
  }

  const relatedArticles = articles
    .filter((item) => item.id !== article.id)
    .slice(0, 3)

  return (
    <div className="article-page">
      <header className="article-header">
        <div className="container article-header-inner">
          <a href="/" className="article-logo" aria-label="Katchit home">
            <img src="/assets/Frame.svg" alt="Katchit" />
          </a>
          <a href="/#blog" className="article-back-link">
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to blogs
          </a>
        </div>
      </header>

      <main>
        <section className="article-hero">
          <div className="article-hero-glow" />
          <div className="container article-hero-grid">
            <motion.div
              className="article-hero-copy"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="article-breadcrumb">
                <a href="/">Home</a>
                <i className="bi bi-chevron-right" aria-hidden="true" />
                <a href="/#blog">Blog</a>
                <i className="bi bi-chevron-right" aria-hidden="true" />
                <span>{article.category}</span>
              </div>
              <span className="article-category">{article.category}</span>
              <h1>{article.title}</h1>
              <p>{article.excerpt}</p>
              <div className="article-meta">
                <span>
                  <i className="bi bi-clock" aria-hidden="true" />
                  {article.readTime}
                </span>
                <span>
                  <i className="bi bi-journal-check" aria-hidden="true" />
                  Katchit Editorial
                </span>
              </div>
            </motion.div>

            <motion.div
              className="article-hero-image"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              <img src={article.image} alt={article.title} />
              <span className="article-image-badge">
                <i className="bi bi-stars" aria-hidden="true" />
                The Katchit Edit
              </span>
            </motion.div>
          </div>
        </section>

        <section className="article-body-section">
          <div className="container article-layout">
            <article className="article-content">
              <p className="article-lead">
                Good plans feel effortless when the important decisions are made
                early. This Katchit guide gives you a simple place to start and a
                few practical details that make a real difference.
              </p>

              <h2>What to keep in mind</h2>
              <p>
                There is no single formula that works for everyone. Focus on the
                experience you want, keep the people involved in mind and use
                these essentials as your checklist.
              </p>

              <div className="article-points">
                {article.points.map((point, index) => (
                  <div className="article-point" key={point}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <p>{point}</p>
                  </div>
                ))}
              </div>

              <blockquote>
                <i className="bi bi-quote" aria-hidden="true" />
                <p>{article.takeaway}</p>
              </blockquote>

              <h2>Make it easier with Katchit</h2>
              <p>
                Katchit is designed to connect everyday needs in one place—from
                discovering local restaurants and planning celebrations to
                catering and courier support. Explore the app when you are ready
                to turn the idea into a plan.
              </p>

              <div className="article-cta">
                <div>
                  <span>Ready when you are</span>
                  <h3>Find your next experience on Katchit</h3>
                </div>
                <a
                  href="https://play.google.com/store/apps/details?id=com.katchit.customer_app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Explore the app
                  <i className="bi bi-arrow-up-right" aria-hidden="true" />
                </a>
              </div>
            </article>

            <aside className="article-sidebar">
              <div className="article-sidebar-card">
                <span className="article-sidebar-label">In this story</span>
                <h3>Quick takeaways</h3>
                <ul>
                  {article.points.map((point) => (
                    <li key={point}>
                      <i className="bi bi-check2-circle" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        <section className="related-section">
          <div className="container">
            <div className="related-heading">
              <div>
                <span>Keep reading</span>
                <h2>More from the Katchit Edit</h2>
              </div>
              <a href="/#blog">View all stories</a>
            </div>
            <div className="related-grid">
              {relatedArticles.map((item) => (
                <a href={`/blog/${item.slug}`} className="related-card" key={item.id}>
                  <img src={item.image} alt="" />
                  <div>
                    <span>{item.category}</span>
                    <h3>{item.title}</h3>
                    <p>{item.readTime}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="article-footer">
        <div className="container">
          <img src="/assets/Frame.svg" alt="Katchit" />
          <p>Fresh stories, local discoveries and easier everyday plans.</p>
          <a href="/#blog">Explore all blogs</a>
        </div>
      </footer>
    </div>
  )
}

export default BlogArticle
